import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { useProviderAuth } from '@/hooks/auth'
import {
  CREDENTIAL_OPTIONS,
  onboardingCredentialsSchema,
  type CredentialType,
  type OnboardingCredentialsData,
} from '@/schemas/onboarding-credentials'
import { MVP_STATES, MVP_STATES_NOTE } from '@/data/mvp-states'
import type { CredentialsStepData, ProviderType } from '@/types/onboarding'

const STEP_INDEX = 1

function emptyLicense() {
  return { state: '', license_number: '' }
}

function getStoredData(stepData: Record<string, unknown>): CredentialsStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = onboardingCredentialsSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

function defaultCredentials(providerType: ProviderType | undefined): CredentialType {
  // provider_type (MD/DO/NP/PA) is a subset of CredentialType (also DNP, PhD).
  // If the registration flow captured it, use it as the sensible default so
  // the provider doesn't have to re-pick.
  return providerType ?? 'MD'
}

export function CredentialsStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()
  const { provider } = useProviderAuth()

  // Seed form defaults exactly once from the state at mount. useState's
  // initializer is guaranteed to run once; useMemo([]) is a hint React may
  // re-run, so we prefer useState here for one-time initialization.
  const [defaults] = useState<OnboardingCredentialsData>(() => {
    const stored = getStoredData(state.stepData)
    if (stored) {
      return {
        credentials: stored.credentials,
        npi: stored.npi,
        state_licenses:
          stored.state_licenses.length > 0 ? stored.state_licenses : [emptyLicense()],
      }
    }
    return {
      credentials: defaultCredentials(provider?.provider_type),
      npi: '',
      state_licenses: [emptyLicense()],
    }
  })

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<OnboardingCredentialsData>({
    resolver: zodResolver(onboardingCredentialsSchema),
    mode: 'onChange',
    defaultValues: defaults,
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'state_licenses',
  })

  const credentials = watch('credentials')

  const onSubmit = (data: OnboardingCredentialsData) => {
    setStepData(STEP_INDEX, data satisfies CredentialsStepData)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !isValid,
    onClick: handleSubmit(onSubmit),
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Credentials</h2>
        <p className="mt-1 text-sm text-slate-600">
          Tell us how you're credentialed and where you're licensed to practice.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        aria-label="Credentials form"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="credentials">Credentials</Label>
            <Select
              value={credentials}
              onValueChange={(val) =>
                setValue('credentials', val as CredentialType, { shouldValidate: true })
              }
            >
              <SelectTrigger id="credentials" aria-label="Credentials">
                <SelectValue placeholder="Select credentials" />
              </SelectTrigger>
              <SelectContent>
                {CREDENTIAL_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="npi">NPI</Label>
            <Input id="npi" placeholder="1234567890" inputMode="numeric" {...register('npi')} />
            {errors.npi && (
              <p role="alert" className="text-sm text-red-600">
                {errors.npi.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>State licenses</Label>
              <p className="text-sm text-slate-500">{MVP_STATES_NOTE}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append(emptyLicense())}
            >
              <Plus className="mr-1 h-4 w-4" /> Add state
            </Button>
          </div>

          <div className="space-y-3">
            {fields.map((field, index) => {
              const selectedState = watch(`state_licenses.${index}.state`)
              return (
                <div key={field.id} className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor={`state-${index}`}>State</Label>
                    <Select
                      value={selectedState}
                      onValueChange={(val) =>
                        setValue(`state_licenses.${index}.state`, val, {
                          shouldValidate: true,
                        })
                      }
                    >
                      <SelectTrigger
                        id={`state-${index}`}
                        aria-label={`State ${index + 1}`}
                      >
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {MVP_STATES.map((s) => (
                          <SelectItem key={s.code} value={s.code}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.state_licenses?.[index]?.state && (
                      <p role="alert" className="text-sm text-red-600">
                        {errors.state_licenses[index]?.state?.message}
                      </p>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label htmlFor={`license-${index}`}>License number</Label>
                    <Input
                      id={`license-${index}`}
                      placeholder="WA-MD-123456"
                      {...register(`state_licenses.${index}.license_number`)}
                    />
                    {errors.state_licenses?.[index]?.license_number && (
                      <p role="alert" className="text-sm text-red-600">
                        {errors.state_licenses[index]?.license_number?.message}
                      </p>
                    )}
                  </div>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-7"
                      aria-label={`Remove state license ${index + 1}`}
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Visually hidden submit so pressing Enter inside the form triggers
            handleSubmit via browser default. Distinct accessible name from
            the wizard's forward-action button to keep test queries
            unambiguous. */}
        <button type="submit" aria-label="Submit credentials form" className="sr-only" tabIndex={-1} />
      </form>
    </div>
  )
}
